import { WordHelp } from "@/components/lesson/WordHelp";
import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Hand, Send, Leaf, Volume2, VolumeX, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { rehypeWordSpans, useReadAlong, wordIndexOf } from "@/lib/readAlong";
import { KidAvatar } from "@/components/live/KidAvatar";
import { VoiceInput } from "@/components/buddy/VoiceInput";

const REACTIONS = ["👏", "❤️", "😄", "🤔", "🌟", "👍"];

const Table = ({ roster }) => (
  <div className="relative mx-auto w-full max-w-[520px] aspect-[16/10]" data-testid="live-table">
    <div className="absolute inset-[18%] rounded-[50%] bg-[#E3EBE5] border-4 border-[#C8DCCB] grid place-items-center">
      <div className="text-center"><span className="w-14 h-14 mx-auto rounded-full bg-[#2C4A3E] grid place-items-center"><Leaf className="w-6 h-6 text-[#F2D49B]" /></span><p className="text-xs mt-1 text-[#2C4A3E] font-medium">Aria · AI teacher</p></div>
    </div>
    {roster.map((p, i) => {
      const ang = (2 * Math.PI * i) / Math.max(roster.length, 1) - Math.PI / 2;
      return (
        <div key={p.id} className={`absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center transition-opacity ${p.online ? "" : "opacity-40"}`}
          style={{ left: `${50 + 44 * Math.cos(ang)}%`, top: `${50 + 42 * Math.sin(ang)}%` }} data-testid={`live-seat-${p.id}`} data-online={p.online}>
          <div className="relative"><KidAvatar a={p.avatar} className={`w-14 h-14 ${p.me ? "ring-4 ring-[#C87D53] rounded-full" : ""}`} />
            {p.hand && <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#F2C14E] grid place-items-center float-up" data-testid={`live-hand-${p.id}`}><Hand className="w-4 h-4" /></span>}</div>
          <span className="text-[11px] mt-1 font-medium text-[#2C4A3E]">{p.me ? "You" : p.name}</span>
        </div>
      );
    })}
  </div>
);

const Stage = ({ seg, sound, setSound, ended }) => {
  const ra = useReadAlong();
  const el = useRef(null);
  const read = (from = 0) => ra.play(seg.text, "sage", el.current, seg.id, from).catch(() => {});
  useEffect(() => { if (seg && sound) setTimeout(() => read(), 60); else ra.stop(); }, [seg?.id, sound]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <section className="rounded-3xl bg-white/80 border border-[#E6DEC3] p-6" data-testid="live-stage" data-translate>
      <div className="flex justify-between items-center"><p className="text-xs uppercase tracking-widest text-stone-500">Aria is saying</p>
        <button onClick={() => setSound(!sound)} data-testid="live-sound-toggle" data-state={sound ? "on" : "off"} className="text-stone-500 hover:text-[#2C4A3E]">{sound ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}</button></div>
      <WordHelp containerRef={el} onOpen={ra.pause} />
      <div ref={el} onClick={(e) => { const i = wordIndexOf(e); if (i != null && seg) read(i); }} className="lesson-md text-lg mt-3 rw-active" data-keep-active="1" data-testid="live-stage-text">
        {seg ? <ReactMarkdown rehypePlugins={[rehypeWordSpans]}>{seg.text}</ReactMarkdown> : !ended && <p className="text-stone-500">Class will begin soon. Take a slow breath and get comfy.</p>}
      </div>
    </section>
  );
};

const Chat = ({ msgs, live, cid, onSent }) => {
  const [text, setText] = useState("");
  const end = useRef(null);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth", block: "nearest" }); }, [msgs.length]);
  const send = async (kind, t = "") => {
    try { const r = await http.post(`/live/classes/${cid}/messages`, { kind, text: t }); onSent(r.data); if (r.data.blocked) toast.error(r.data.reason); if (kind === "chat") setText(""); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <section className="rounded-3xl bg-[#F7F3EB] border border-[#E6DEC3] flex flex-col h-[560px]" data-testid="live-chat">
      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {msgs.filter((m) => m.kind !== "hand").map((m) => (
          <div key={m.id} data-testid={`live-msg-${m.kind}`} className={`text-sm rounded-2xl px-3 py-2 ${m.kind === "teacher" ? "bg-[#2C4A3E] text-white" : m.blocked ? "bg-red-50 text-red-700 border border-red-200" : "bg-white"}`}>
            <b className="mr-1">{m.name}:</b>{m.kind === "reaction" ? <span className="text-xl">{m.text}</span> : m.blocked ? <span><ShieldAlert className="inline w-3.5 h-3.5" /> Not sent. {m.reason}</span> : <span className="chat-md inline"><ReactMarkdown components={{ p: "span" }}>{m.text}</ReactMarkdown></span>}
          </div>
        ))}
        <div ref={end} />
      </div>
      {live ? (
        <div className="p-3 border-t border-[#E6DEC3] space-y-2">
          <div className="flex gap-1.5">{REACTIONS.map((r) => <button key={r} onClick={() => send("reaction", r)} data-testid={`live-react-${REACTIONS.indexOf(r)}`} className="w-9 h-9 rounded-full bg-white hover:scale-110 transition-transform">{r}</button>)}
            <button onClick={() => send("hand")} data-testid="live-hand-btn" className="ml-auto inline-flex items-center gap-1 px-3 rounded-full bg-[#F2C14E] text-sm"><Hand className="w-4 h-4" /> Raise hand</button></div>
          <form onSubmit={(e) => { e.preventDefault(); text.trim() && send("chat", text); }} className="flex gap-2">
            <VoiceInput onText={(t) => setText((x) => (x ? `${x} ${t}` : t))} />
            <input value={text} onChange={(e) => setText(e.target.value)} maxLength={300} placeholder="Share kindly…" data-testid="live-chat-input" className="flex-1 min-w-0 px-4 py-2 rounded-full bg-white border border-[#E6DEC3] text-sm focus:outline-none focus:border-[#2C4A3E]" />
            <button data-testid="live-send-btn" className="w-10 h-10 rounded-full bg-[#C87D53] text-white grid place-items-center"><Send className="w-4 h-4" /></button>
          </form>
        </div>
      ) : <p className="p-4 text-sm text-stone-500 border-t border-[#E6DEC3]" data-testid="live-chat-closed">Chat is open only while class is live.</p>}
    </section>
  );
};

export default function LiveRoom() {
  const { classId } = useParams();
  const [d, setD] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [sound, setSound] = useState(true);
  const [err, setErr] = useState("");
  const after = useRef("");
  useEffect(() => {
    let alive = true, t;
    const poll = async () => {
      try {
        const r = await http.get(`/live/classes/${classId}/feed`, { params: { after: after.current } });
        if (!alive) return;
        setD(r.data);
        if (r.data.messages.length) { after.current = r.data.messages[r.data.messages.length - 1].created_at; setMsgs((m) => [...m, ...r.data.messages.filter((x) => !m.some((y) => y.id === x.id))]); }
      } catch (e) { if (alive) setErr(formatApiError(e)); }
      if (alive) t = setTimeout(poll, 2500);
    };
    poll();
    return () => { alive = false; clearTimeout(t); };
  }, [classId]);
  if (err && !d) return <div className="max-w-5xl mx-auto px-6 py-20 text-stone-600" data-testid="live-room-error">{err}</div>;
  if (!d) return <div className="max-w-5xl mx-auto px-6 py-20 text-stone-500">Joining class…</div>;
  const c = d.class;
  const seg = [...msgs].reverse().find((m) => m.kind === "teacher");
  const left = Math.max(0, Math.round((new Date(c.starts_at).getTime() + c.duration_min * 60000 - Date.now()) / 60000));
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8 pb-20" data-testid="live-room" data-status={c.status}>
      <Link to="/live" className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#2C4A3E]" data-testid="live-room-back"><ArrowLeft className="w-4 h-4" /> Live classes</Link>
      <div className="flex flex-wrap items-end justify-between gap-3 mt-4">
        <div><span className={`text-xs font-semibold uppercase tracking-[0.25em] ${c.status === "live" ? "text-[#C0392B]" : "text-[#C87D53]"}`} data-testid="live-room-status">{c.status === "live" ? `● Live · ${left} min left` : c.status === "ended" ? "Class ended" : `Starts ${new Date(c.starts_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`}</span>
          <h1 className="font-display text-3xl sm:text-4xl text-[#2C4A3E] mt-1">{c.topic}</h1></div>
        <p className="text-sm text-stone-500">{c.grade_label} · {c.subject} · {d.roster.filter((p) => p.online).length}/{d.roster.length} here</p>
      </div>
      <div className="grid lg:grid-cols-12 gap-8 mt-6">
        <div className="lg:col-span-7 space-y-6"><Table roster={d.roster} /><Stage seg={c.status === "ended" ? null : seg} sound={sound} setSound={setSound} ended={c.status === "ended"} />
          {c.status === "ended" && <div className="flex flex-wrap items-center gap-4"><p className="font-display text-xl text-[#2C4A3E]" data-testid="live-ended-note">Thank you for learning together today! See you next time.</p><Link to={`/live/${classId}/memory`} data-testid="live-memory-link" className="px-5 py-2.5 rounded-full bg-[#C87D53] text-white text-sm">Open my Memory Book</Link></div>}</div>
        <div className="lg:col-span-5"><Chat msgs={msgs} live={c.status === "live"} cid={classId} onSent={(m) => setMsgs((x) => (x.some((y) => y.id === m.id) ? x : [...x, m]))} /></div>
      </div>
    </div>
  );
}
